/**
 * HeartbeatService
 *
 * Monitors SIP connection health and triggers reconnection when needed.
 * Based on legacy PhoneStore.setSIPKeepAlive() implementation.
 *
 * Checks WebSocket transport state every 15 seconds and attempts
 * reconnection if disconnected. This prevents silent disconnections
 * after browser idle time and ensures connection stays alive.
 */

import type { SipService } from 'Modules/softphone/services/SipService';
import { lifecycleLogger as logger } from 'Modules/softphone/utils/logger';
import { HEARTBEAT_INTERVAL_MS } from './index.constants';

export class HeartbeatService {
  private static instance: HeartbeatService;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private isRunning = false;

  private constructor() {
    logger.info('HeartbeatService initialized');
  }

  static getInstance(): HeartbeatService {
    if (!HeartbeatService.instance) {
      HeartbeatService.instance = new HeartbeatService();
    }
    return HeartbeatService.instance;
  }

  /**
   * Start heartbeat monitoring
   *
   * @param sipService - SipService instance to monitor
   * @param onDisconnected - Callback to trigger when disconnection is detected
   */
  start(sipService: SipService, onDisconnected: () => void): void {
    if (this.isRunning) {
      logger.warn('Heartbeat already running, skipping start');
      return;
    }

    logger.info('Starting heartbeat monitoring', {
      intervalMs: HEARTBEAT_INTERVAL_MS,
    });

    this.intervalId = setInterval(() => {
      const userAgent = sipService.getUserAgent();

      if (!userAgent) {
        logger.debug('Heartbeat: No UserAgent, skipping check');
        return;
      }

      const transportState = userAgent.transport?.state;

      if (transportState === 'Disconnected') {
        logger.warn(
          'Heartbeat detected disconnection, triggering reconnection callback'
        );
        onDisconnected();
      } else {
        logger.debug('Heartbeat check OK', { transportState });
      }
    }, HEARTBEAT_INTERVAL_MS);

    this.isRunning = true;
  }

  /**
   * Stop heartbeat monitoring
   */
  stop(): void {
    if (!this.isRunning) {
      logger.debug('Heartbeat not running, skipping stop');
      return;
    }

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.isRunning = false;
    logger.info('Heartbeat monitoring stopped');
  }

  /**
   * Check if heartbeat is currently running
   */
  getIsRunning(): boolean {
    return this.isRunning;
  }
}
